import { useEffect, useRef } from 'react';

import { clampNonNegativeInt, NumericInput } from '../../NumericInput';
import type {
  PreviewSampleContext,
  PreviewSampleFieldVisibility,
  PreviewSampleLimits,
  PreviewSampleLineContext,
} from '../types';

/**
 * 音声プレビューの「プレビュー条件」入力パネル (共通部品) — Issue #439 / #445。
 *
 * ## なぜ共通部品にするか
 *
 * プレビュー条件 (サンプル遅延 / 線名 / 乗換駅 / 方面) は
 *
 *   1. 個別フィールドのプレビュー (`AudioPreviewField`)
 *   2. コンテンツ全体・放送地点まるごとの通し試聴 (`BroadcastPointForm`)
 *
 * の **両方** から指定できる必要がある (「プレビューやコンテンツ全体をプレビューで、
 * プレビュー時の条件の入力ができるようにしてほしい」)。
 * 入力欄をそれぞれの画面で複製すると
 *
 *   - 一方だけ項目が増減して「画面によって指定できる条件が違う」
 *   - 遅延の分 ⇔ 秒 換算がズレて「入力した分数と鳴る音声が食い違う」
 *
 * といった不整合が起きるため、UI と換算規則を本部品に一元化する。
 *
 * ## 表示規則
 *
 * `fields` (凡例定義から `resolveSampleFields()` / `resolvePointSampleFields()` で
 * 導出) が true の項目だけを表示する。**本番で読み上げられない項目は入力させない**
 * (例: whole_line モードでは乗換駅・方面の欄を出さない)。
 *
 * ## 複数条件は「いま設定できる項目」に従う (Issue #445 / レビュー反映)
 *
 * 接続線 (線名) と方面は元データが配列なので、プレビュー条件も複数指定できる必要がある。
 * ただし **行数・方面欄数はユーザーが自由に増減するものではなく、`limits`
 * (コンテンツ設定から導出した入力枠) に従う**。
 *
 * 設定に無い接続線・方面を入力できてしまうと、サーバ (`preview_render.py`) は
 * `properties.lines[]` しか辿らないため音声に反映されず
 * 「入力したのに鳴らない」という分かりにくさが生じるためである。
 * 逆に接続線を 2 本設定すればプレビュー条件も自動で 2 行になるので、
 * 「複数の入力項目がある場合はプレビュー条件も同じ多重度で指定できる」要望も満たせる。
 *
 * 各行の見出しには設定名 (`limit.label` = 線区愛称 / route_id) を出し、
 * 「どの接続線の条件を編集しているか」が一目で分かるようにする。
 *
 * 後方互換のため、**1 行目には index 無しの `data-testid` も併記** する
 * (既存テスト / E2E が `preview-sample-route-name-<suffix>` を参照している)。
 *
 * ## 遅延分数は「自列車」と「接続線」を分けて指定する (Issue #468)
 *
 * 従来は遅延の入力欄が「遅延 (分)」1 つだけで、
 *
 *   - それが自列車の遅れなのか接続線の遅れなのか判別できない
 *   - **接続線ごとの遅延分数を指定できない** (2 本目の接続線だけ 10 分で試聴、が不可能)
 *
 * という 2 つの分かりにくさがあった。実際サーバ (`preview_render.py`) は
 * `sample_context.lines[j].delay_seconds` を接続線ごとに読めるため、
 * 入力欄が無いだけで「指定できるはずの条件が指定できない」状態だった。
 *
 * そこで
 *
 *   1. 全体の遅延欄は、自列車の枠がある場合に **「自列車遅延 (分)」** と明示する
 *      (自列車の枠が無いコンテンツでは従来どおり「遅延 (分)」= 全体の既定値)
 *   2. 接続線の各行に **「接続線遅延 (分)」** を追加し、その接続線だけ別の遅延で鳴らせる
 *
 * とする。接続線遅延を未入力の行は全体値へフォールバックするため
 * (サーバの `_sample_line_delay_seconds()` と同じ規則)、1 クリック試聴の手軽さは変わらない。
 *
 * ## 開いたときに見える位置までスクロールする
 *
 * 「プレビュー条件」トグルは試聴ボタンの横にあり、通し試聴では編集モーダルの最下部にある。
 * 開くとパネルが下に伸びるだけでスクロール領域の外に出てしまい、増えたことに気づきにくかった。
 * そのため、パネルが表示された (マウントされた) ときに `scrollIntoView({ block: 'nearest' })` で
 * 「はみ出した分だけ」下へスクロールする。すでに全体が見えているときは動かさない。
 * 開閉は呼び出し側が `{open ? <PreviewSamplePanel /> : null}` で制御しているため、
 * マウント時の 1 回だけ実行すれば「開いたとき」になる (入力中の再レンダーでは動かない)。
 */
export function PreviewSamplePanel({
  testIdSuffix,
  fields,
  sample,
  limits,
  onChange,
  onLineChange,
  onDirectionChange,
}: {
  /**
   * `data-testid` のサフィックス。
   * 個別プレビューは `self-train-delay-0-1` のようなフィールド識別子、
   * 放送地点まるごとプレビューは `point` を渡す。
   */
  testIdSuffix: string;
  /** 表示する入力欄の種類 (凡例に載っている項目のみ true) */
  fields: PreviewSampleFieldVisibility;
  /** 現在のサンプル条件 (表示値) */
  sample: PreviewSampleContext;
  /** 1 項目の変更通知 (呼び出し側で「ユーザーが編集した」と記録する) */
  onChange: <K extends keyof PreviewSampleContext>(key: K, value: PreviewSampleContext[K]) => void;
  /** 接続線条件 (`lines[index]`) の 1 項目の変更通知 (Issue #445) */
  onLineChange: <K extends keyof PreviewSampleLineContext>(
    index: number,
    key: K,
    value: PreviewSampleLineContext[K],
  ) => void;
  /** 方面 (`lines[lineIndex].directionNames[dirIndex]`) の変更通知 (Issue #445) */
  onDirectionChange: (lineIndex: number, dirIndex: number, value: string) => void;
  /**
   * 指定できる入力枠 (接続線の件数 / 各接続線の方面欄数) — Issue #445 (レビュー反映)。
   *
   * `deriveContentSampleLimits()` / `derivePointSampleLimits()` が
   * **コンテンツ設定から** 導出したものを渡す。行数・方面欄数はこの枠に従い、
   * ユーザーが任意に増減することはできない (設定に無い条件を入力させない)。
   * 省略時は表示中の `sample.lines` をそのまま枠として扱う。
   */
  limits?: PreviewSampleLimits;
}): React.JSX.Element {
  // 線名 / 方面のどちらかが使えるコンテンツでのみ繰り返し入力を出す。
  const showLines = fields.showRouteName || fields.showDirectionName;
  /*
   * 表示する行は「設定できる接続線」ぶん (`limits.lines`)。
   * 枠が未指定 (旧経路) の場合は表示値をそのまま使い、最低 1 行は出す
   * (何も入力できない状態を避ける)。
   */
  const limitLines = limits?.lines;
  const lineCount =
    limitLines !== undefined ? limitLines.length : Math.max(1, sample.lines?.length ?? 0);
  const lines = Array.from({ length: lineCount }, (_, index) => sample.lines?.[index] ?? {});

  /*
   * 全体の遅延欄の見出し (Issue #468)。
   *
   * 自列車の枠 (`kind: 'self'`) がある場合は「自列車遅延 (分)」と明示し、
   * 接続線の各行に出る「接続線遅延 (分)」と対比できるようにする。
   * 自列車の枠が無い (接続線遅延のみ / 枠が未指定の旧経路) 場合は、
   * この欄が「接続線遅延を指定しなかった行に使われる全体値」なので
   * 従来どおり「遅延 (分)」と表示する (誤ったラベルを出さない)。
   */
  const hasSelfLine = (limitLines ?? []).some((limit) => limit.kind === 'self');
  const delayLabel = hasSelfLine ? '自列車遅延 (分)' : '遅延 (分)';

  // 開いた (= マウントされた) ときに、パネル全体が見える位置までスクロールする。
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = panelRef.current;
    // jsdom など scrollIntoView を持たない環境では何もしない。
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, []);

  return (
    <div
      ref={panelRef}
      className="preview-sample-panel"
      data-testid={`preview-sample-panel-${testIdSuffix}`}
      style={{
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        margin: '6px 0',
        padding: '6px 8px',
        border: '1px solid #d0d7de',
        borderRadius: 4,
      }}
    >
      {fields.showDelay ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Issue #468: 自列車の枠があるときは「自列車遅延 (分)」と明示する。 */}
          <span className="hint">{delayLabel}</span>
          {/*
            「0 を消して 15 と打ちたい」ができるよう共通の `NumericInput` を使う。
            素朴な `Number(e.target.value) || 0` だと空欄が 0 に戻り、消せず「015」になっていた。
            値は分で扱い、親へは秒で渡す。空欄は 0 分として扱い、blur で 0 表示へ戻る。
          */}
          <NumericInput
            min={0}
            step={1}
            style={{ width: 80 }}
            aria-label={`プレビュー条件 ${delayLabel} (${testIdSuffix})`}
            data-testid={`preview-sample-delay-${testIdSuffix}`}
            value={Math.floor((sample.delaySeconds ?? 0) / 60)}
            normalizeValue={clampNonNegativeInt}
            onChange={(minutes) => onChange('delaySeconds', minutes * 60)}
          />
        </label>
      ) : null}
      {fields.showFromStation ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">乗換駅</span>
          <input
            type="text"
            style={{ width: 100 }}
            aria-label={`プレビュー条件 乗換駅 (${testIdSuffix})`}
            data-testid={`preview-sample-from-station-${testIdSuffix}`}
            value={sample.fromStation ?? ''}
            onChange={(e) => onChange('fromStation', e.target.value)}
          />
        </label>
      ) : null}
      {fields.showTransferStation ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">乗換駅</span>
          <input
            type="text"
            style={{ width: 100 }}
            aria-label={`プレビュー条件 乗換駅名 (${testIdSuffix})`}
            data-testid={`preview-sample-transfer-station-${testIdSuffix}`}
            value={sample.transferStation ?? ''}
            onChange={(e) => onChange('transferStation', e.target.value)}
          />
        </label>
      ) : null}
      {fields.showDepartureTime ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">発車見込み時刻 (24時間表記)</span>
          {/*
            SBI-6: 発車見込み案内の `{departure_time}` の元になる時刻。
            本番は接続便の時刻表から引くが、試聴では手入力した時刻を使う。
            `type="time"` は 24 時間表記の `HH:MM` を返す (サーバの書式と一致)。
            空欄にするとサーバ既定 (12:13) で鳴る。
          */}
          <input
            type="time"
            style={{ width: 110 }}
            aria-label={`プレビュー条件 発車見込み時刻 (${testIdSuffix})`}
            data-testid={`preview-sample-departure-time-${testIdSuffix}`}
            value={sample.departureTime ?? ''}
            onChange={(e) => onChange('departureTime', e.target.value)}
          />
        </label>
      ) : null}
      {fields.showHeadsign ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">行先</span>
          <input
            type="text"
            style={{ width: 100 }}
            placeholder="例: 大宮"
            aria-label={`プレビュー条件 行先 (${testIdSuffix})`}
            data-testid={`preview-sample-headsign-${testIdSuffix}`}
            value={sample.headsign ?? ''}
            onChange={(e) => onChange('headsign', e.target.value)}
          />
        </label>
      ) : null}
      {/*
        接続線 (線名) × 方面 の入力欄 (Issue #445 / レビュー反映)。

        行数・方面欄数は `limits` (= コンテンツ設定から導出した入力枠) に従うため、
        接続線を 2 本設定すれば 2 行、方面を 2 つ設定すれば方面欄が 2 つ出る。
        設定に無い接続線・方面は入力できない (鳴らない条件を入力させない)。
      */}
      {showLines ? (
        <div className="preview-sample-lines" style={{ flexBasis: '100%' }}>
          {lines.map((line, lineIndex) => {
            const limit = limitLines?.[lineIndex];
            // 見出しは設定名 (線区愛称 / route_id) を優先し、「どの接続線か」を明示する。
            const lineLabel = limit?.label ?? `${lineIndex + 1}`;
            /*
             * 方面欄の数も設定に従う。枠が未指定 (旧経路) なら表示値の件数を使い、
             * 1 件も無ければ 1 欄だけ出す (方面を使うコンテンツなのに入力できないのを防ぐ)。
             */
            const directionCount =
              limit !== undefined
                ? limit.maxDirections
                : Math.max(1, line.directionNames?.length ?? 0);
            return (
              <div
                // 行は設定 (接続線) の並び順に 1:1 対応するので index で識別できる。
                key={lineIndex}
                data-testid={`preview-sample-line-${testIdSuffix}-${lineIndex}`}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'flex-end',
                  marginBottom: 4,
                }}
              >
                {/*
                  接続線ごとの遅延分数 (Issue #468)。

                  `kind: 'connecting'` の行にだけ出す。自路線の行 (`kind: 'self'`) は
                  全体の「自列車遅延 (分)」で指定するので重複した欄を出さない。
                  遅延を読み上げないコンテンツ (`showDelay: false`) でも出さない。

                  未入力 (undefined) の行はサーバ側で全体値へフォールバックするため、
                  「この接続線だけ 10 分で試聴したい」ときにだけ入力すればよい。
                */}
                {fields.showDelay && (limit?.kind ?? 'connecting') === 'connecting' ? (
                  <label
                    style={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%' }}
                  >
                    <span className="hint">接続線遅延 (分)</span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      style={{ width: 90, maxWidth: '100%' }}
                      aria-label={`プレビュー条件 接続線遅延 ${lineIndex + 1} (${testIdSuffix})`}
                      data-testid={`preview-sample-line-delay-${testIdSuffix}-${lineIndex}`}
                      value={
                        line.delaySeconds === undefined
                          ? ''
                          : Math.floor(line.delaySeconds / 60).toString()
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        /*
                         * 空文字は「この接続線は指定しない」= 全体値へ戻す意思表示なので
                         * `undefined` にする (0 分と混同すると「0分遅れ」で鳴ってしまう)。
                         */
                        if (raw.trim() === '') {
                          onLineChange(lineIndex, 'delaySeconds', undefined);
                          return;
                        }
                        const minutes = Math.max(0, Math.floor(Number(raw) || 0));
                        onLineChange(lineIndex, 'delaySeconds', minutes * 60);
                      }}
                    />
                  </label>
                ) : null}

                {fields.showRouteName ? (
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      maxWidth: '100%',
                      flex: '1 1 180px',
                    }}
                  >
                    <span className="hint">線名 ({lineLabel})</span>
                    <input
                      type="text"
                      style={{ width: '100%' }}
                      aria-label={`プレビュー条件 線名 ${lineIndex + 1} (${testIdSuffix})`}
                      data-testid={`preview-sample-route-name-${testIdSuffix}-${lineIndex}`}
                      value={line.routeName ?? ''}
                      onChange={(e) => onLineChange(lineIndex, 'routeName', e.target.value)}
                    />
                  </label>
                ) : null}

                {fields.showDirectionName
                  ? Array.from({ length: directionCount }, (_, dirIndex) => {
                      // 方面の見出しも設定名 (`directions[].direction_name`) を優先する。
                      const directionLabel =
                        limit?.directionLabels?.[dirIndex] || `${dirIndex + 1}`;
                      return (
                        <label
                          key={dirIndex}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            maxWidth: '100%',
                          }}
                        >
                          <span className="hint">方面 ({directionLabel})</span>
                          <input
                            type="text"
                            style={{ width: 100, maxWidth: '100%' }}
                            aria-label={`プレビュー条件 方面 ${lineIndex + 1}-${dirIndex + 1} (${testIdSuffix})`}
                            data-testid={`preview-sample-direction-name-${testIdSuffix}-${lineIndex}-${dirIndex}`}
                            value={line.directionNames?.[dirIndex] ?? ''}
                            onChange={(e) => onDirectionChange(lineIndex, dirIndex, e.target.value)}
                          />
                        </label>
                      );
                    })
                  : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {/*
        後方互換用の index 無し testid (Issue #445)。
        既存テスト / E2E が `preview-sample-route-name-<suffix>` /
        `preview-sample-direction-name-<suffix>` を参照しているため、
        1 行目の値を同じ挙動で編集できる隠しフィールドとして残す。
      */}
      {showLines && fields.showRouteName ? (
        <input
          type="text"
          hidden
          aria-label={`プレビュー条件 線名 (${testIdSuffix})`}
          data-testid={`preview-sample-route-name-${testIdSuffix}`}
          value={lines[0]?.routeName ?? sample.routeName ?? ''}
          onChange={(e) => onLineChange(0, 'routeName', e.target.value)}
        />
      ) : null}
      {showLines && fields.showDirectionName ? (
        <input
          type="text"
          hidden
          aria-label={`プレビュー条件 方面 (${testIdSuffix})`}
          data-testid={`preview-sample-direction-name-${testIdSuffix}`}
          value={lines[0]?.directionNames?.[0] ?? sample.directionName ?? ''}
          onChange={(e) => onDirectionChange(0, 0, e.target.value)}
        />
      ) : null}

      <span className="hint" style={{ flexBasis: '100%' }}>
        試聴用のサンプル値です。実際の放送では運行情報から取得した値が使われます。
        入力欄は現在の設定内容 (接続線・方面) に合わせて表示されます。
        {/* Issue #468: 遅延欄が複数あるので「未入力なら全体値」という規則を明記する。 */}
        「接続線遅延 (分)」を空欄にした接続線は、{delayLabel}の値で試聴します。
      </span>
    </div>
  );
}
